import { Share } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MarkerIcon } from 'src/components';
import { homeToStoreAgencyInfoSelector } from '../../HomToStoreContainer/store';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface Props {
  clientDemandId: string;
  tel: string;
}

export const FromHomeToStoreSuccess: React.FC<Props> = () => {
  const data = useSelector(homeToStoreAgencyInfoSelector);

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view_from_home_to_store_success', {
        event_category: 'Navigation',
        event_label: data.slug,
      });
    }
  }, [data]);

  const handleItineraryClick = useCallback(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'click_itineraire_from_home_to_store', {
        event_category: 'CTA',
        agency_name: data?.name,
        agency_city: data?.city?.name,
      });
    }
  }, [data]);

  const getUrlFromWithSlug = useMemo(() => {
    const currentUrl = window.location.href;
    const match = currentUrl.match(/^(https?:\/\/[^\/]+\/home\/[^\/]+)/);

    return match[1];
  }, []);

  const getSharedLink = useMemo(() => {
    let shareUrl = getUrlFromWithSlug;

    const country = data?.city?.country?.code;

    if (country) {
      let fullCountry = 'france';

      switch (country.toLowerCase()) {
        case 'fr':
        case 'france':
          fullCountry = 'france';
          break;
        case 'ma':
        case 'maroc':
          fullCountry = 'maroc';
          break;
      }

      shareUrl = `https://smartcolis.com/${fullCountry}`;
    }

    return shareUrl;
  }, [data, getUrlFromWithSlug]);

  const sharedLinkWithFriends = useCallback(async () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'share_service_with_friends_from_home_to_store', {
        event_category: 'CTA',
        agency_name: data?.name,
        agency_city: data?.city?.name,
      });
    }

    let shareUrl = getSharedLink;

    const shareTitle = 'Découvrez ce service SmartColis';
    const shareText = `Service d’envoi de colis international entre le Maroc et la France à petit prix.`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'native_share_success_from_home_to_store', {
            event_category: 'Engagement',
            agency_name: data?.name,
          });
        }
      } catch (error) {
        console.error('Erreur lors du partage:', error);
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'native_share_failed_from_home_to_store', {
            event_category: 'Error',
            error_message: error.message,
            agency_name: data?.name,
          });
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Lien copié dans le presse-papiers !');
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'copy_link_success_from_home_to_store', {
            event_category: 'Engagement',
            agency_name: data?.name,
          });
        }
      } catch (err) {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'copy_link_failed_from_home_to_store', {
            event_category: 'Error',
            error_message: err.message,
            agency_name: data?.name,
          });
        }
      }
    }
  }, [getSharedLink, data?.city?.name, data?.name]);

  useEffect(() => {
    if (!data) {
      const currentUrl = window.location.href;
      const match = currentUrl.match(/^(https?:\/\/[^\/]+\/home\/[^\/]+)/);

      if (match) {
        window.location.href = match[1];
      }
    }
  }, [data]);

  const agencyInfo = useMemo(() => {
    if (!data) {
      return;
    }

    return `${data?.name} ${data?.address} ${data?.zip}, ${data?.city.name}`;
  }, [data]);

  return (
    <div className="flex justify-center items-start">
      <div>
        <p className="text-2xl text-primary text-center">Merci de votre confiance !</p>
        <p className="text-center text-xl mt-2">
          Vous pouvez désormais déposer vos colis à l’agence{' '}
          <p className="text-primary font-bold">
            {data?.name} à : <br /> {data?.address}, {data?.zip} {data?.city.name}.
          </p>
        </p>
        <div>
          <p className="bg-gray-200 rounded px-2 py-2 text-black my-2">
            En vous présentant à l’agence, donnez à l’agent de comptoir votre numéro de téléphone pour qu’il retrouve
            votre demande en ligne.
          </p>
        </div>
        <div>
          <a
            onClick={handleItineraryClick}
            className="bg-blue-600 text-white px-4 py-2 rounded mt-4 flex flex-row items-center justify-center space-x-1"
            href={`https://www.google.com/maps/search/?api=1&query=${data?.lat},${data?.lang}`}
            target="_blank"
            rel="noreferrer"
          >
            <MarkerIcon color="white" />
            <span className="text-xl">Itinéraire</span>
          </a>
        </div>

        <div>
          <button
            className="bg-indigo-400 text-white px-4 py-2 rounded mt-4 flex flex-row items-center justify-center space-x-1 w-full"
            onClick={sharedLinkWithFriends}
          >
            <Share /> Partager ce service à un ami
          </button>
          <p className="text-gray-500 text-md px-2 py-2 text-center">
            <a href={getSharedLink} target="_blank" rel="noreferrer">
              {getSharedLink}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
